// utils/audioValidation.js - Frontend pre-upload validation (CLEANED - ERRORS ONLY)

import { getPlanLimits, PLAN_LIMITS } from '../constants/planLimits';

/**
 * Validate audio/video file BEFORE uploading to backend
 * This saves bandwidth and time by catching issues early
 * 
 * @param {File} file - The audio/video file to validate
 * @param {string} userPlan - User's plan type ('free', 'starter', 'creator', 'professional')
 * @returns {Promise<Object>} - { valid: boolean, error: string | null, duration: number, fileSize: number }
 */
export const validateAudioFile = async (file, userPlan = 'free') => {
  try {
    

    // 1. Validate file exists
    if (!file) {
      return {
        valid: false,
        error: 'No file selected',
        duration: 0,
        fileSize: 0
      };
    }

    // 2. Check file type
    const isAudio = file.type.startsWith('audio/');
    const isVideo = file.type.startsWith('video/');
    
    if (!isAudio && !isVideo) {
      return {
        valid: false,
        error: 'Invalid file type. Please upload an audio or video file.',
        duration: 0,
        fileSize: file.size
      };
    }

    // 3. Check file size (before duration check - instant validation)
    const fileSizeMB = file.size / (1024 * 1024);
    const MAX_UPLOAD_SIZE = 100; // 100MB (multer limit)
    
    if (fileSizeMB > MAX_UPLOAD_SIZE) {
      return {
        valid: false,
        error: `File too large: ${fileSizeMB.toFixed(1)}MB. Maximum upload size is ${MAX_UPLOAD_SIZE}MB.`,
        duration: 0,
        fileSize: file.size
      };
    }

    // 4. Get audio/video duration
    const duration = await getMediaDuration(file);
    
    if (!duration || duration === 0) {
      return {
        valid: false,
        error: 'Could not determine media duration. Please try a different file.',
        duration: 0,
        fileSize: file.size
      };
    }

    // 5. Check duration against plan limits
    const planLimits = getPlanLimits(userPlan);
    const maxDuration = planLimits.maxAudioDuration;
    const durationMinutes = Math.ceil(duration / 60);
    const maxMinutes = Math.floor(maxDuration / 60);

    

    if (duration > maxDuration) {
      // Generate upgrade suggestion based on ACTUAL plan structure
      const upgradeInfo = getUpgradeMessage(duration, userPlan);

   

      return {
        valid: false,
        error: upgradeInfo.message,
        duration,
        fileSize: file.size,
        requiresUpgrade: upgradeInfo.canUpgrade,
        suggestedPlan: upgradeInfo.suggestedPlan
      };
    }

    // 6. All validations passed
    

    return {
      valid: true,
      error: null,
      duration,
      fileSize: file.size,
      fileSizeMB: fileSizeMB.toFixed(1),
      durationMinutes
    };

  } catch (error) {
   
    return {
      valid: false,
      error: `Validation failed: ${error.message}`,
      duration: 0,
      fileSize: file.size
    };
  }
};

/**
 * Generate upgrade message based on ACTUAL plan limits
 * @param {number} durationSeconds - Duration in seconds
 * @param {string} currentPlan - Current user plan
 * @returns {Object} - { message: string, canUpgrade: boolean, suggestedPlan: string | null }
 */
const getUpgradeMessage = (durationSeconds, currentPlan) => {
  const currentPlanData = getPlanLimits(currentPlan);
  const durationMinutes = Math.ceil(durationSeconds / 60);
  const maxMinutes = Math.floor(currentPlanData.maxAudioDuration / 60);

  // Find the next plan that can handle this duration
  const suggestedPlan = getSuggestedPlan(durationSeconds);

  // Check if file is too long for ANY plan
  if (durationSeconds > 3600) {
    return {
      message: `Audio too long (${durationMinutes} minutes). Maximum supported duration is 60 minutes. Please split your file into shorter segments.`,
      canUpgrade: false,
      suggestedPlan: null
    };
  }

  // Build upgrade message based on current plan
  let upgradeText = '';

  if (currentPlan === 'free') {
    if (durationSeconds <= 2400) {
      // 10-40 min: Suggest Creator
      upgradeText = ' Upgrade to Creator for 40-minute uploads!';
    } else {
      // 40-60 min: Suggest Professional
      upgradeText = ' Upgrade to Professional for 60-minute uploads!';
    }
  } else if (currentPlan === 'starter') {
    if (durationSeconds <= 2400) {
      // 10-40 min: Suggest Creator
      upgradeText = ' Upgrade to Creator for 40-minute uploads!';
    } else {
      // 40-60 min: Suggest Professional
      upgradeText = ' Upgrade to Professional for 60-minute uploads!';
    }
  } else if (currentPlan === 'creator') {
    // Creator limit is 40 min, so if exceeded, need Professional (60 min)
    if (durationSeconds <= 3600) {
      upgradeText = ' Upgrade to Professional for 60-minute uploads!';
    }
  } else if (currentPlan === 'professional') {
    // Already at max plan
    upgradeText = ' You are on the highest plan. Please use a shorter file.';
  }

  return {
    message: `Audio too long for ${currentPlanData.name} plan. Your file: ${durationMinutes} minutes. Maximum: ${maxMinutes} minutes.${upgradeText}`,
    canUpgrade: suggestedPlan !== null,
    suggestedPlan
  };
};

/**
 * Get media duration using browser's Audio/Video API
 * This runs in the browser, no backend call needed!
 * 
 * @param {File} file - Audio or video file
 * @returns {Promise<number>} - Duration in seconds
 */
const getMediaDuration = (file) => {
  return new Promise((resolve, reject) => {
    const isVideo = file.type.startsWith('video/');
    const mediaElement = isVideo 
      ? document.createElement('video')
      : document.createElement('audio');

    const objectURL = URL.createObjectURL(file);
    
    // Set timeout to avoid hanging
    const timeout = setTimeout(() => {
      URL.revokeObjectURL(objectURL);
      reject(new Error('Timeout getting media duration'));
    }, 10000); // 10 second timeout

    mediaElement.preload = 'metadata';
    
    mediaElement.onloadedmetadata = () => {
      clearTimeout(timeout);
      URL.revokeObjectURL(objectURL);
      
      const duration = mediaElement.duration;
      
      if (!duration || isNaN(duration) || duration === Infinity) {
        reject(new Error('Invalid media duration'));
        return;
      }
      
      resolve(duration);
    };

    mediaElement.onerror = (error) => {
      clearTimeout(timeout);
      URL.revokeObjectURL(objectURL);
      reject(new Error('Failed to load media file'));
    };

    mediaElement.src = objectURL;
  });
};

/**
 * Suggest which plan user needs based on duration and ACTUAL limits
 * Uses actual PLAN_LIMITS from constants
 * 
 * @param {number} durationSeconds - Duration in seconds
 * @returns {string | null} - Plan name or null if too long
 */
const getSuggestedPlan = (durationSeconds) => {
  // Check each plan in order from lowest to highest
  const plans = ['free', 'starter', 'creator', 'professional'];
  
  for (const planName of plans) {
    const planData = PLAN_LIMITS[planName];
    if (durationSeconds <= planData.maxAudioDuration) {
      return planName;
    }
  }
  
  // File is longer than any plan supports
  return null;
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} - "45.2 MB", "892 KB", etc.
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Format duration for display
 * @param {number} seconds - Duration in seconds
 * @returns {string} - "5:23", "12:45", "1:23:45"
 */
export const formatDurationDisplay = (seconds) => {
  if (!seconds || seconds === 0) return '0:00';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

/**
 * ✅ REMOVED - No more warning messages
 * Only actual errors are shown now
 */
export const checkFileSizeReasonable = (bytes) => {
  return {
    reasonable: true,
    warning: null
  };
};